'use client';

import React, { useMemo } from 'react';
import LazyLoad from 'react-lazyload';
import { stringifyUrl } from 'query-string';
import { ThemeProvider } from 'styled-components';
import { wagwebTheme } from '@/theme/wagwebTheme';
import { ArticleCost } from '../ArticleCost';
import { ArticleCoverPhoto } from '../ArticleCoverPhoto';
import { ArticleSection } from '../ArticleSection';
import { ArticleSectionContentList } from '../ArticleSectionContentList';
import { ArticleSectionTitle } from '../ArticleSectionTitle';
import { ArticleTitle } from '../ArticleTitle';
import { Flex } from '../Flex';
import { HealthArticleBanner } from '../HealthArticleBanner';
import { HealthArticleUGCVirtualList } from '../HealthArticleUGCVirtualList';
import { HealthRating } from '../HealthRating';
import { Link } from '../Link';
import { RelatedArticles } from '../RelatedArticles';
import { ScrollToTop } from '../ScrollToTop';
// TODO: Hiding this for now. Check NV-2705 for a detailed description
// import { ShareOptions } from '../ShareOptions';

import { HealthArticleProps } from './index';
import { AnimalTypeSlug, HealthArticleType, HEALTH_ARTICLE_ADVICE_SECTION, QueryStringKeys, Routes, UTMMedium, UTMSource, UTMTerm } from '../../constants';
import { DataTestIds } from '../../constants/data-testids';
import { AskAVetChatButton } from '../AskAVetChatButton';
import { NeedAdviceBanner } from '../NeedAdviceBanner';
import { PetInsurerBouncerAndWagQuoteForm } from '../PetInsurerBouncerAndWagQuoteForm';
import { initializeQuoteEngine } from '../PetInsurer/WagQuoteFormScript';
import { useGetURLQuery } from '../../hooks/useGetURLQuery';
import { ArticleMeta } from '../ArticleMeta';
import { ArticleAuthorBlock } from '../ArticleAuthorBlock/ArticleAuthorBlock';
import { WagCompareHealthArticleAdvertCard } from '../HealthArticleAdvertCard/Compare/Compare';
import { PettedHealthArticleAdvertCard } from '../HealthArticleAdvertCard/petted/Petted';
import { ReworkedMarkdown } from '../Markdown/Reworked';
import { ArticleBreadcrumbs } from '../WagWeb/ArticleBreadcrumbs/ArticleBreadcrumbs';
import { WagWebContainer } from '../WagWeb/WagWebContainer/WagWebContainer';
import { Box } from '../../wag-react/next-components/box';
import { Button } from '../../wag-react/next-components/button';
export type HealthArticleContentProps = Omit<HealthArticleProps, 'updateAdviceList'>;
export const HealthArticleContent = ({
  value: {
    adviceSectionTitle,
    adviceList,
    author,
    articlePhotoSrc,
    body,
    contentList,
    breadcrumbItems,
    healthExperienceList,
    healthRating,
    title,
    relatedArticles,
    cost,
    videoUrl,
    isReviewed,
    ignoreDefinedCategories
  },
  articleType,
  metaTitle,
  hasNextPageVetAdvice,
  isFetchingVetAdvice,
  onClickSeeMoreVetAdviceButton,
  nextPageUrl,
  onClickYes,
  onClickNo,
  slug,
  animalTypeSlug
}: HealthArticleContentProps) => {
  const queryParams = useGetURLQuery();
  const utmMeta = {
    articleType,
    slug
  };
  const handlePetInsuranceBouncerLoad = () => {
    /**
     * Wrap in a setTimeout to avoid race condition with the
     * bouncer opening up
     *
     * Note that the timeout duration isn't based on anything
     */
    setTimeout(() => {
      initializeQuoteEngine({
        ...queryParams,
        [QueryStringKeys.UTMSource]: UTMSource.Wag,
        [QueryStringKeys.UTMMedium]: UTMMedium.Web,
        [QueryStringKeys.UTMTerm]: UTMTerm.MobileBouncer,
        [QueryStringKeys.UTMCampaign]: slug,
        [QueryStringKeys.UTMContent]: articleType
      });
    }, 500);
  };
  const petInsurerQuoteBannerLink = stringifyUrl({
    url: Routes.PetInsuranceQuoteLink,
    query: {
      [QueryStringKeys.UTMSource]: UTMSource.Wag,
      [QueryStringKeys.UTMMedium]: UTMMedium.Web,
      [QueryStringKeys.UTMTerm]: UTMTerm.ContentBanner,
      [QueryStringKeys.UTMCampaign]: slug,
      [QueryStringKeys.UTMContent]: articleType
    }
  });

  // Make sure that there is content to be displayed
  // since we're using it to specify the minimum height per content
  // to avoid CLS
  const validContentList = contentList.filter(content => content.content);
  const pageHasCost = pageCost => pageCost && pageCost.avgCost;
  const adviceAndExperienceList = useMemo(() => [...adviceList, ...healthExperienceList], [adviceList, healthExperienceList]);

  /**
   * If there's no animalTypeSlug, it's from the /condition/:slug (dog, cat)
   * If there's a animalTypeSlug, it's from the /:animal/condition/:slug (horse, reptile, bird, etc)
   */
  const isDogOrCatArticle = !animalTypeSlug || animalTypeSlug === AnimalTypeSlug.Cat || animalTypeSlug === AnimalTypeSlug.Dog;
  return <>
      <ThemeProvider theme={wagwebTheme} data-sentry-element="ThemeProvider" data-sentry-source-file="HealthArticleContent.tsx">
        <Box backgroundColor="#F8FBF9" pt="24px" pb={['36px', '36px', '40px']} data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
          <WagWebContainer data-sentry-element="WagWebContainer" data-sentry-source-file="HealthArticleContent.tsx">
            <Flex mt={0} pb={3} data-sentry-element="Flex" data-sentry-source-file="HealthArticleContent.tsx">
              <Box width={1} data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
                <ArticleBreadcrumbs list={breadcrumbItems} data-sentry-element="ArticleBreadcrumbs" data-sentry-source-file="HealthArticleContent.tsx" />
                <ArticleTitle mt="32px" data-sentry-element="ArticleTitle" data-sentry-source-file="HealthArticleContent.tsx">
                  {title}
                </ArticleTitle>

                {!!author && <ArticleMeta mt="24px" author={author?.name} reviewedBy={author?.reviewedBy} publishedDate={author?.publishedDate} editedDate={author?.editedDate} />}

                {/* TODO: May need to add additional Container DOM here */}

                {healthRating.score && isReviewed && <HealthRating value={healthRating} />}

                {/*
                 TODO: Hiding this for now. Check NV-2705 for a detailed description
                 <ShareOptions />
                 */}
              </Box>

            </Flex>

            {!ignoreDefinedCategories ? <Box height={[null, null, '53px']} mt={['16px', '16px', '24px']}>
                <LazyLoad offset={400} once>
                  <HealthArticleBanner articleType={articleType} />
                </LazyLoad>
              </Box> : null}
          </WagWebContainer>
        </Box>

        <WagWebContainer data-sentry-element="WagWebContainer" data-sentry-source-file="HealthArticleContent.tsx">
          <WagWebContainer display={['block', null, 'grid']} gridTemplateColumns="auto 300px" gridColumnGap="50px" pt="40px" pb="100px" px="0px" data-sentry-element="WagWebContainer" data-sentry-source-file="HealthArticleContent.tsx">
            <Box minHeight="1000px" data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
              <ArticleCoverPhoto display={['flex']} src={articlePhotoSrc} borderRadius="16px" alt={metaTitle} data-sentry-element="ArticleCoverPhoto" data-sentry-source-file="HealthArticleContent.tsx" />
              {/* Display 'body' content returned specifically for Treatment Articles */}
              {articleType !== HealthArticleType.Condition && body.map(block => <ReworkedMarkdown key={block.value}>
                    {block.value}
                  </ReworkedMarkdown>)}
              {/* Give each section a minimum of 300; This is a rough estimate */}
              <Box minHeight={`${validContentList.length * 300}px`} data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
                <ArticleSectionContentList value={contentList} renderBannerContent={() => isDogOrCatArticle ? <PettedHealthArticleAdvertCard buttonHref={petInsurerQuoteBannerLink} mt="30px" /> : <NeedAdviceBanner href={Routes.WagServicesWagHealth} titleCopyProps={{
                fontSize: '24px'
              }} />} renderVideoContent={() => articleType === HealthArticleType.Condition && <>
                        {body.map(block => <ReworkedMarkdown key={block.value}>
                            {block.value}
                          </ReworkedMarkdown>)}
                        {videoUrl && <ReworkedMarkdown>
                            {videoUrl}
                          </ReworkedMarkdown>}
                      </>} renderMobileContent={() => <>
                      {cost && cost.avgCost && <ArticleCost utmMeta={utmMeta} value={{
                  ...cost
                }} />}
                      <WagCompareHealthArticleAdvertCard buttonHref={petInsurerQuoteBannerLink} />
                    </>} data-sentry-element="ArticleSectionContentList" data-sentry-source-file="HealthArticleContent.tsx" />
              </Box>

              {/* Mobile/Tablet only */}
              <Box display={['block', 'block', 'none']} my="24px" data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
                {pageHasCost(cost) && <>
                    <ArticleCost utmMeta={utmMeta} value={{
                  ...cost
                }} />
                    <ScrollToTop />
                  </>}
              </Box>

              {!!adviceAndExperienceList?.length && <ArticleSection mt={0} id={HEALTH_ARTICLE_ADVICE_SECTION} mx="unset">
                  <ArticleSectionTitle mt={0}>
                    {adviceSectionTitle}
                  </ArticleSectionTitle>

                  <HealthArticleUGCVirtualList items={adviceAndExperienceList} adviceProps={{
                onClickYes,
                onClickNo
              }}>
                    <Button display={hasNextPageVetAdvice ? 'block' : 'none'} my="16px" mx="auto" variant="outlined" disabled={isFetchingVetAdvice} onClick={onClickSeeMoreVetAdviceButton} data-testid={DataTestIds.HealthArticle.SeeMoreBtn}>
                      See more
                      {/* //TODO: replace href link with correct link */}
                      <Link href={nextPageUrl} rel="nofollow" style={{
                    position: 'absolute',
                    left: '-1000px',
                    top: '-1000px',
                    visibility: 'hidden',
                    pointerEvents: 'none'
                  }}>
                        See more
                      </Link>
                    </Button>
                  </HealthArticleUGCVirtualList>
                </ArticleSection>}

              {author && <ArticleSection id="author" mt={0}>
                  <LazyLoad offset={400} once>

                    <ArticleAuthorBlock writtenBy={{
                  image: author.src,
                  name: author.name
                }} reviewedBy={{
                  name: author.reviewedBy
                }} relatedArticles={author.relatedArticles} />

                  </LazyLoad>
                </ArticleSection>}

              <ArticleSection id="related" mt={0} data-sentry-element="ArticleSection" data-sentry-source-file="HealthArticleContent.tsx">
                <RelatedArticles value={relatedArticles} data-sentry-element="RelatedArticles" data-sentry-source-file="HealthArticleContent.tsx" />
              </ArticleSection>

            </Box>

            {/* Desktop only */}
            <Box display={['none', null, 'block']} data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
              <Box position="sticky" top="104px" mb="56px" data-sentry-element="Box" data-sentry-source-file="HealthArticleContent.tsx">
                {pageHasCost(cost) && <ArticleCost value={{
                ...cost
              }} utmMeta={utmMeta} />}
                <WagCompareHealthArticleAdvertCard mb="30px" buttonHref={petInsurerQuoteBannerLink} data-sentry-element="WagCompareHealthArticleAdvertCard" data-sentry-source-file="HealthArticleContent.tsx" />
              </Box>
            </Box>
          </WagWebContainer>
        </WagWebContainer>

        {isDogOrCatArticle ? <PetInsurerBouncerAndWagQuoteForm onLoad={handlePetInsuranceBouncerLoad} /> : <AskAVetChatButton display="flex" tooltip={{
        text: 'More questions? A vet is standing by',
        width: '180px'
      }} onClick={() => {
        window.open(Routes.WagServicesWagHealth, '_blank');
      }} />}
      </ThemeProvider>
    </>;
};